import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

import { Box } from './Box';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { Stack } from './Stack';
import { Typography } from './Typography';

export const AudioPlayer = ({ url }) => {
  const theme = useTheme();
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const waveformRef = useRef();
  const wavesurferRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState('0:00');
  const [currentTimestamp, setCurrentTimestamp] = useState('0:00');

  useEffect(() => {
    if (waveformRef.current) {
      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        barWidth: 3,
        barHeight: 1.5,
        cursorWidth: 0,
        width: smDown ? 60 : 100,
        height: 35,
        waveColor: theme.palette.primary.main,
        cursorColor: theme.palette.primary.lightest,
        url: url,
      });

      const formatFromSeconds = (seconds: number): string => {
        const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
        const mins = Math.floor(safe / 60);
        const secs = Math.floor(safe % 60);
        const zeroPad = (num: number) => String(num).padStart(2, '0');
        return `${mins}:${zeroPad(secs)}`;
      };

      const setDur = () =>
        setDuration(formatFromSeconds(wavesurfer.getDuration()));
      wavesurfer.on('ready', setDur);
      wavesurfer.on('decode', setDur);

      wavesurfer.on('audioprocess', () => {
        setCurrentTimestamp(formatFromSeconds(wavesurfer.getCurrentTime()));
      });

      wavesurferRef.current = wavesurfer;

      return () => wavesurfer?.destroy();
    }
  }, [
    theme.palette.primary.lightest,
    theme.palette.primary.main,
    url,
    waveformRef,
    smDown,
  ]);

  const toggleWavesurfer = () => {
    if (isPlaying) {
      setIsPlaying(false);
      wavesurferRef.current.pause();
    } else {
      setIsPlaying(true);
      wavesurferRef.current.play();
    }
  };
  return (
    <Stack
      spacing={1}
      sx={{
        p: 1,
        borderRadius: 1,
        borderWidth: 1,
        borderColor: 'divider',
        borderStyle: 'solid',
      }}
      direction="row"
      bgcolor="background.level1"
      alignItems="center"
      onClick={(event) => {
        event?.stopPropagation();
      }}
    >
      <IconButton color="primary" onClick={toggleWavesurfer}>
        {!isPlaying && <Icon icon={icon({ name: 'play', style: 'solid' })} />}
        {isPlaying && <Icon icon={icon({ name: 'pause', style: 'solid' })} />}
      </IconButton>
      <Box ref={waveformRef} />
      <Typography color="text.primary" variant="body2" sx={{ px: 1 }}>
        {isPlaying ? currentTimestamp : duration}
      </Typography>
    </Stack>
  );
};
